<template>
  <div class="assistant-admin" dir="rtl">
    <div class="page-heading">
      <div>
        <h2>گفتگوهای دستیار فروش</h2>
        <p>بررسی سؤال‌های مشتریان، پاسخ‌های مدل و تعامل با محصولات پیشنهادی</p>
      </div>
      <button class="refresh-btn" type="button" @click="refreshAll">به‌روزرسانی</button>
    </div>

    <section class="settings-card">
      <div class="settings-heading">
        <div>
          <h3>تنظیمات OpenRouter</h3>
          <p>مدل‌ها و کلید دسترسی دستیار فروش را بدون نیاز به تغییر فایل‌های سرور مدیریت کنید.</p>
        </div>
        <span :class="['key-status', { ready: assistantSettings.api_key_configured }]">
          {{ assistantSettings.api_key_configured ? 'کلید فعال' : 'کلید ثبت نشده' }}
        </span>
      </div>

      <div class="settings-grid">
        <label class="toggle-field">
          <input v-model="settingsForm.enabled" type="checkbox">
          <span>دستیار فروش فعال باشد</span>
        </label>
        <label>
          <span>درصد انتشار</span>
          <input v-model.number="settingsForm.rollout_percent" type="number" min="0" max="100">
        </label>
        <label>
          <span>مدل اصلی</span>
          <input v-model.trim="settingsForm.primary_model" list="openrouter-model-list" dir="ltr" autocomplete="off">
        </label>
        <label>
          <span>مدل جایگزین</span>
          <input v-model.trim="settingsForm.fallback_model" list="openrouter-model-list" dir="ltr" autocomplete="off">
        </label>
        <label class="api-key-field">
          <span>API Key جدید</span>
          <input
            v-model.trim="settingsForm.api_key"
            type="password"
            dir="ltr"
            autocomplete="new-password"
            :placeholder="assistantSettings.api_key_masked || 'sk-or-v1-...'"
          >
          <small>خالی بگذارید تا کلید فعلی حفظ شود. مقدار واقعی کلید هیچ‌وقت از سرور برگردانده نمی‌شود.</small>
        </label>
        <datalist id="openrouter-model-list">
          <option v-for="model in availableModels" :key="model.id" :value="model.id">{{ model.name }}</option>
        </datalist>
      </div>

      <div class="settings-actions">
        <button type="button" :disabled="settingsSaving" @click="saveSettings()">
          {{ settingsSaving ? 'در حال ذخیره...' : 'ذخیره تنظیمات' }}
        </button>
        <button
          v-if="assistantSettings.api_key_source === 'panel'"
          type="button"
          class="danger-outline"
          :disabled="settingsSaving"
          @click="clearPanelApiKey"
        >پاک کردن کلید پنل</button>
        <small v-if="assistantSettings.api_key_source === 'environment'">کلید فعلی از تنظیمات محیط سرور خوانده می‌شود.</small>
        <span v-if="settingsMessage" :class="['settings-message', { error: settingsError }]">{{ settingsMessage }}</span>
      </div>
    </section>

    <section class="settings-card assistant-content-settings">
      <div class="settings-heading">
        <div>
          <h3>پاسخ‌های پیش‌فرض و انتخاب‌های شروع</h3>
          <p>پاسخ‌های ثابت بدون فراخوانی هوش مصنوعی ارسال می‌شوند. ترتیب قوانین، اولویت بررسی آن‌هاست.</p>
        </div>
      </div>

      <div class="assistant-content-grid">
        <div class="config-block">
          <div class="config-block-heading">
            <div>
              <strong>پاسخ‌های پیش‌فرض</strong>
              <small>برای هر پاسخ می‌توانید چند عبارت محرک را در خط‌های جدا وارد کنید.</small>
            </div>
            <button type="button" class="add-config" @click="addDefaultResponse">افزودن پاسخ</button>
          </div>
          <div v-if="!settingsForm.default_responses.length" class="config-empty">هنوز پاسخ پیش‌فرضی تعریف نشده است.</div>
          <article
            v-for="(rule, index) in settingsForm.default_responses"
            :key="`response-${index}`"
            class="config-row response-rule"
          >
            <div class="rule-toolbar">
              <span>قانون {{ formatNumber(index + 1) }}</span>
              <label><input v-model="rule.enabled" type="checkbox"> فعال</label>
              <select v-model="rule.match_type">
                <option value="contains">شامل عبارت باشد</option>
                <option value="exact">دقیقاً برابر باشد</option>
              </select>
              <button type="button" class="remove-config" @click="removeDefaultResponse(index)">حذف</button>
            </div>
            <label>
              <span>عبارت‌های محرک؛ هر عبارت در یک خط</span>
              <textarea v-model="rule.triggers_text" rows="3" dir="auto" placeholder="سلام&#10;سلام وقت بخیر"></textarea>
            </label>
            <label>
              <span>پاسخ پیش‌فرض</span>
              <textarea v-model="rule.answer" rows="4" dir="auto" placeholder="پاسخی که مستقیماً برای کاربر نمایش داده می‌شود"></textarea>
            </label>
          </article>
        </div>

        <div class="config-block">
          <div class="config-block-heading">
            <div>
              <strong>انتخاب‌های اولیه چت</strong>
              <small>این گزینه‌ها پیش از شروع گفتگو در ویجت فروشگاه نمایش داده می‌شوند.</small>
            </div>
            <button type="button" class="add-config" @click="addStarterPrompt">افزودن انتخاب</button>
          </div>
          <div v-if="!settingsForm.starter_prompts.length" class="config-empty">هیچ انتخاب اولیه‌ای نمایش داده نمی‌شود.</div>
          <article
            v-for="(item, index) in settingsForm.starter_prompts"
            :key="`starter-${index}`"
            class="config-row starter-rule"
          >
            <label>
              <span>عنوان دکمه</span>
              <input v-model.trim="item.label" maxlength="80" dir="auto" placeholder="کرم دست برای زمستان">
            </label>
            <label>
              <span>پیامی که ارسال می‌شود</span>
              <input v-model.trim="item.message" maxlength="300" dir="auto" placeholder="یک کرم دست مناسب فصل زمستان پیشنهاد بده">
            </label>
            <button type="button" class="remove-config" @click="removeStarterPrompt(index)">حذف</button>
          </article>
        </div>
      </div>

      <div class="settings-actions">
        <button type="button" :disabled="settingsSaving" @click="saveSettings()">
          {{ settingsSaving ? 'در حال ذخیره...' : 'ذخیره پاسخ‌ها و انتخاب‌ها' }}
        </button>
        <span v-if="settingsMessage" :class="['settings-message', { error: settingsError }]">{{ settingsMessage }}</span>
      </div>
    </section>

    <div class="metrics">
      <article><small>گفتگوها</small><strong>{{ formatNumber(analytics.conversation_count) }}</strong></article>
      <article><small>پیام‌ها</small><strong>{{ formatNumber(analytics.message_count) }}</strong></article>
      <article><small>کلیک محصول</small><strong>{{ formatNumber(analytics.click_count) }}</strong><em>{{ analytics.click_rate || 0 }}٪ نمایش‌ها</em></article>
      <article><small>افزودن به سبد</small><strong>{{ formatNumber(analytics.add_to_cart_count) }}</strong><em>{{ analytics.add_to_cart_rate || 0 }}٪ نمایش‌ها</em></article>
      <article><small>بدون نتیجه</small><strong>{{ formatNumber(analytics.no_result_count) }}</strong><em>{{ analytics.no_result_rate || 0 }}٪ گفتگوها</em></article>
      <article><small>خطا</small><strong>{{ formatNumber(analytics.error_count) }}</strong></article>
      <article><small>میانگین پاسخ</small><strong>{{ formatDuration(analytics.average_latency_ms) }}</strong></article>
      <article><small>مصرف توکن</small><strong>{{ formatNumber(analytics.total_tokens) }}</strong></article>
    </div>

    <div class="filters">
      <input v-model.trim="filters.search" type="search" placeholder="جست‌وجو در متن، نام، موبایل یا ایمیل" @keyup.enter="applyFilters">
      <select v-model="filters.user_type" @change="applyFilters">
        <option value="">همه کاربران</option>
        <option value="member">اعضا</option>
        <option value="guest">مهمان‌ها</option>
      </select>
      <input v-model="filters.date_from" type="date" @change="applyFilters">
      <input v-model="filters.date_to" type="date" @change="applyFilters">
      <label><input v-model="filters.no_result" type="checkbox" @change="applyFilters"> بدون نتیجه</label>
      <label><input v-model="filters.has_error" type="checkbox" @change="applyFilters"> خطادار</label>
      <button type="button" @click="applyFilters">اعمال</button>
      <button class="clear" type="button" @click="clearFilters">پاک کردن</button>
    </div>

    <div class="content-grid" :class="{ 'has-detail': selectedConversation }">
      <section class="conversation-list">
        <div v-if="loading" class="empty-state">در حال دریافت گفتگوها...</div>
        <div v-else-if="!conversations.length" class="empty-state">گفتگویی با این فیلتر پیدا نشد.</div>
        <button
          v-for="item in conversations"
          v-else
          :key="item.conversation_id"
          type="button"
          class="conversation-row"
          :class="{ active: selectedConversation && selectedConversation.conversation_id === item.conversation_id }"
          @click="fetchDetail(item.conversation_id)"
        >
          <span class="identity">
            <strong>{{ item.user_name || 'مهمان' }}</strong>
            <small dir="ltr">{{ item.user_mobile || item.user_email || 'Guest' }}</small>
          </span>
          <span class="preview" dir="auto">{{ item.last_message || 'بدون پیام' }}</span>
          <span class="row-meta">
            <small>{{ formatDate(item.updated_at) }}</small>
            <small>{{ item.message_count }} پیام</small>
            <em v-if="item.add_to_cart_count">{{ item.add_to_cart_count }} سبد</em>
            <em v-if="item.error_count" class="danger">{{ item.error_count }} خطا</em>
          </span>
        </button>

        <div v-if="total" class="pagination">
          <button :disabled="page <= 1" @click="page--; fetchConversations()">قبلی</button>
          <span>صفحه {{ page }} از {{ totalPages }}</span>
          <button :disabled="page >= totalPages" @click="page++; fetchConversations()">بعدی</button>
        </div>
      </section>

      <aside v-if="selectedConversation" class="conversation-detail">
        <header>
          <div>
            <h3>{{ selectedConversation.user_name || 'مهمان' }}</h3>
            <p v-if="selectedConversation.user_mobile" dir="ltr">{{ selectedConversation.user_mobile }}</p>
            <p v-if="selectedConversation.user_email" dir="ltr">{{ selectedConversation.user_email }}</p>
          </div>
          <button type="button" aria-label="بستن" @click="selectedConversation = null">×</button>
        </header>

        <div class="detail-summary">
          <span>شروع: {{ formatDate(selectedConversation.created_at) }}</span>
          <span>آخرین فعالیت: {{ formatDate(selectedConversation.updated_at) }}</span>
          <span v-if="selectedConversation.start_path" dir="ltr">{{ selectedConversation.start_path }}</span>
        </div>

        <div class="timeline">
          <article
            v-for="message in selectedConversation.messages"
            :key="message.id"
            :class="['message', `message-${message.role}`]"
          >
            <div class="message-meta">
              <strong>{{ message.role === 'user' ? 'مشتری' : 'دستیار' }}</strong>
              <small>{{ formatDate(message.created_at) }}</small>
            </div>
            <p dir="auto">{{ message.content }}</p>
            <div v-if="message.products && message.products.length" class="recommended-products">
              <a
                v-for="product in message.products"
                :key="product.id"
                :href="`https://exuni.ir${product.url}`"
                target="_blank"
                rel="noopener"
              >
                <img v-if="product.image" :src="img_source(product.image)" :alt="product.name">
                <span dir="auto">{{ product.name }}</span>
                <small>{{ formatNumber(product.price) }} تومان</small>
              </a>
            </div>
            <details v-if="message.role === 'assistant'">
              <summary>اطلاعات فنی پاسخ</summary>
              <div class="technical">
                <span dir="ltr">{{ message.model_name || 'fallback' }}</span>
                <span>{{ formatDuration(message.latency_ms) }}</span>
                <span>{{ formatNumber(message.total_tokens) }} توکن</span>
                <span v-if="message.error_code" class="danger" dir="ltr">{{ message.error_code }}</span>
              </div>
            </details>
          </article>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
export default {
  name: "AssistantConversations",
  data() {
    return {
      analytics: {
        conversation_count: 0,
        message_count: 0,
        click_count: 0,
        click_rate: 0,
        add_to_cart_count: 0,
        add_to_cart_rate: 0,
        no_result_count: 0,
        no_result_rate: 0,
        error_count: 0,
        average_latency_ms: 0,
        total_tokens: 0,
      },
      conversations: [],
      selectedConversation: null,
      loading: false,
      settingsSaving: false,
      settingsError: false,
      settingsMessage: "",
      assistantSettings: {
        api_key_configured: false,
        api_key_masked: "",
        api_key_source: "missing",
      },
      settingsForm: {
        enabled: true,
        rollout_percent: 100,
        primary_model: "anthropic/claude-sonnet-4.5",
        fallback_model: "openai/gpt-4.1-mini",
        api_key: "",
        default_responses: [],
        starter_prompts: [],
      },
      availableModels: [],
      page: 1,
      limit: 25,
      total: 0,
      filters: {
        search: "",
        user_type: "",
        date_from: "",
        date_to: "",
        no_result: false,
        has_error: false,
      },
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
  },
  created() {
    this.setDocumentTitle && this.setDocumentTitle("گفتگوهای دستیار فروش");
    this.refreshAll();
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString("fa-IR");
    },
    formatDuration(value) {
      if (!value) return "—";
      return value >= 1000 ? `${(value / 1000).toFixed(1)} ثانیه` : `${value} میلی‌ثانیه`;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("fa-IR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
    refreshAll() {
      this.fetchSettings();
      this.fetchModels();
      this.fetchAnalytics();
      this.fetchConversations();
      if (this.selectedConversation) this.fetchDetail(this.selectedConversation.conversation_id);
    },
    applyFilters() {
      this.page = 1;
      this.selectedConversation = null;
      this.fetchConversations();
    },
    clearFilters() {
      this.filters = { search: "", user_type: "", date_from: "", date_to: "", no_result: false, has_error: false };
      this.applyFilters();
    },
    fetchAnalytics() {
      this.request({
        url: this.endpoint("api/v1/assistant/admin/analytics"),
        method: "get",
        params: { days: 30 },
        success: data => { this.analytics = data; },
      });
    },
    fetchSettings() {
      this.request({
        url: this.endpoint("api/v1/assistant/admin/settings"),
        method: "get",
        success: data => {
          this.assistantSettings = data;
          this.settingsForm = {
            enabled: data.enabled,
            rollout_percent: data.rollout_percent,
            primary_model: data.primary_model,
            fallback_model: data.fallback_model,
            api_key: "",
            default_responses: (data.default_responses || []).map(rule => ({
              triggers_text: (rule.triggers || []).join("\n"),
              answer: rule.answer || "",
              match_type: rule.match_type || "contains",
              enabled: rule.enabled !== false,
            })),
            starter_prompts: (data.starter_prompts || []).map(item => ({
              label: item.label || "",
              message: item.message || "",
            })),
          };
        },
      });
    },
    fetchModels() {
      this.request({
        url: this.endpoint("api/v1/assistant/admin/models"),
        method: "get",
        success: data => { this.availableModels = data.results || []; },
      });
    },
    saveSettings(extra = {}) {
      this.settingsSaving = true;
      this.settingsMessage = "";
      this.settingsError = false;
      const payload = {
        ...this.settingsForm,
        default_responses: this.settingsForm.default_responses.map(rule => ({
          triggers: String(rule.triggers_text || "")
            .split(/\r?\n|،|,/)
            .map(value => value.trim())
            .filter(Boolean),
          answer: String(rule.answer || "").trim(),
          match_type: rule.match_type,
          enabled: rule.enabled !== false,
        })),
        starter_prompts: this.settingsForm.starter_prompts.map(item => ({
          label: String(item.label || "").trim(),
          message: String(item.message || "").trim(),
        })),
        ...extra,
      };
      if (!payload.api_key) delete payload.api_key;
      this.request({
        url: this.endpoint("api/v1/assistant/admin/settings"),
        method: "put",
        data: payload,
        success: data => {
          this.assistantSettings = data;
          this.settingsForm.api_key = "";
          this.settingsSaving = false;
          this.settingsMessage = "تنظیمات با موفقیت ذخیره شد.";
        },
        error: error => {
          this.settingsSaving = false;
          this.settingsError = true;
          this.settingsMessage = (error && error.response && error.response.data && error.response.data.detail)
            || "ذخیره تنظیمات انجام نشد. مقادیر واردشده را بررسی کنید.";
        },
      });
    },
    clearPanelApiKey() {
      if (!window.confirm("کلید ذخیره‌شده در پنل پاک شود؟ در صورت وجود، کلید محیط سرور جایگزین خواهد شد.")) return;
      this.settingsForm.api_key = "";
      this.saveSettings({ clear_api_key: true });
    },
    addDefaultResponse() {
      if (this.settingsForm.default_responses.length >= 30) return;
      this.settingsForm.default_responses.push({
        triggers_text: "",
        answer: "",
        match_type: "contains",
        enabled: true,
      });
    },
    removeDefaultResponse(index) {
      this.settingsForm.default_responses.splice(index, 1);
    },
    addStarterPrompt() {
      if (this.settingsForm.starter_prompts.length >= 8) return;
      this.settingsForm.starter_prompts.push({ label: "", message: "" });
    },
    removeStarterPrompt(index) {
      this.settingsForm.starter_prompts.splice(index, 1);
    },
    fetchConversations() {
      this.loading = true;
      const params = {
        limit: this.limit,
        offset: (this.page - 1) * this.limit,
      };
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key]) params[key] = this.filters[key];
      });
      this.request({
        url: this.endpoint("api/v1/assistant/admin/conversations"),
        method: "get",
        params,
        success: data => {
          this.conversations = data.results || [];
          this.total = data.count || 0;
          this.loading = false;
        },
        error: () => { this.loading = false; },
      });
    },
    fetchDetail(id) {
      this.request({
        url: this.endpoint(`api/v1/assistant/admin/conversations/${id}`),
        method: "get",
        success: data => { this.selectedConversation = data; },
      });
    },
  },
};
</script>

<style scoped>
.assistant-admin { min-height:100vh; padding:12px; color:#30343b; background:#f6f7f9; }
.page-heading { display:flex; justify-content:space-between; align-items:center; padding:18px; margin-bottom:12px; background:#fff; border:1px solid #e8ebef; border-radius:16px; }
.page-heading h2 { margin:0 0 5px; font-size:18px; }.page-heading p{margin:0;color:#79808b;font-size:12px}
.refresh-btn,.filters button,.pagination button { border:0; border-radius:9px; padding:8px 12px; color:#fff; background:#293241; cursor:pointer; }
.settings-card{padding:16px;margin-bottom:12px;background:#fff;border:1px solid #e8ebef;border-radius:16px}.settings-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:15px}.settings-heading h3{margin:0 0 5px;font-size:15px}.settings-heading p{margin:0;color:#79808b;font-size:10px}.key-status{padding:5px 9px;border-radius:999px;background:#fff1f1;color:#b64149;font-size:9px;white-space:nowrap}.key-status.ready{background:#eaf8f1;color:#168463}.settings-grid{display:grid;grid-template-columns:160px 120px minmax(220px,1fr) minmax(220px,1fr);gap:10px;align-items:start}.settings-grid label{display:flex;flex-direction:column;gap:6px;color:#555d67;font-size:10px}.settings-grid input{min-height:36px;padding:6px 9px;border:1px solid #dfe3e8;border-radius:9px;font:inherit}.settings-grid input[dir=ltr]{text-align:left}.toggle-field{flex-direction:row!important;align-items:center;min-height:36px}.toggle-field input{min-height:auto;width:17px;height:17px}.api-key-field{grid-column:3/5}.api-key-field small{color:#8b929c;font-size:8px}.settings-actions{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:13px}.settings-actions button{border:0;border-radius:9px;padding:8px 13px;color:#fff;background:#293241;cursor:pointer}.settings-actions button:disabled{opacity:.55;cursor:default}.settings-actions .danger-outline{color:#b33d46;background:#fff;border:1px solid #e9b9bd}.settings-actions small{color:#7e8792;font-size:9px}.settings-message{color:#168463;font-size:10px}.settings-message.error{color:#c73f48}
.assistant-content-grid{display:grid;grid-template-columns:1.15fr .85fr;gap:14px}.config-block{min-width:0;padding:13px;border:1px solid #e7eaee;border-radius:13px;background:#fafbfc}.config-block-heading{display:flex;align-items:flex-start;justify-content:space-between;gap:10px;margin-bottom:11px}.config-block-heading>div{display:flex;flex-direction:column;gap:4px}.config-block-heading strong{font-size:12px}.config-block-heading small{color:#818995;font-size:9px;line-height:1.7}.add-config{flex:none;border:0;border-radius:8px;padding:7px 10px;color:#fff;background:#596579;cursor:pointer;font:inherit;font-size:9px}.config-empty{padding:25px 10px;text-align:center;color:#8a929d;font-size:10px;border:1px dashed #d9dde3;border-radius:10px}.config-row{padding:11px;margin-top:9px;border:1px solid #e1e5ea;border-radius:11px;background:#fff}.config-row label{display:flex;flex-direction:column;gap:5px;color:#59616c;font-size:9px}.config-row input,.config-row textarea,.config-row select{width:100%;min-height:34px;padding:7px 9px;border:1px solid #dfe3e8;border-radius:8px;background:#fff;font:inherit;box-sizing:border-box}.config-row textarea{resize:vertical;line-height:1.8}.response-rule{display:grid;grid-template-columns:minmax(160px,.7fr) minmax(220px,1.3fr);gap:10px}.rule-toolbar{grid-column:1/-1;display:flex;align-items:center;gap:9px}.rule-toolbar>span{margin-left:auto;font-size:10px;font-weight:700}.rule-toolbar label{display:flex;flex-direction:row;align-items:center;gap:4px}.rule-toolbar input{width:15px;min-height:auto}.rule-toolbar select{width:auto;min-height:30px;padding:4px 7px;font-size:9px}.remove-config{border:1px solid #edc3c7;border-radius:7px;padding:5px 8px;color:#b43f49;background:#fff;cursor:pointer;font:inherit;font-size:9px}.starter-rule{display:grid;grid-template-columns:minmax(110px,.7fr) minmax(170px,1.3fr) auto;gap:8px;align-items:end}.starter-rule .remove-config{margin-bottom:2px;min-height:32px}.assistant-content-settings>.settings-actions{border-top:1px solid #eceff2;padding-top:12px}
.metrics { display:grid; grid-template-columns:repeat(8,minmax(110px,1fr)); gap:8px; margin-bottom:12px; overflow:auto; }
.metrics article { min-width:110px; padding:13px; background:#fff; border:1px solid #e8ebef; border-radius:13px; }
.metrics small { display:block; color:#8b929c; font-size:10px; }.metrics strong{display:block;margin-top:5px;font-size:16px}.metrics em{display:block;margin-top:3px;color:#89929d;font-size:8px;font-style:normal}
.filters { display:flex; align-items:center; gap:7px; flex-wrap:wrap; padding:12px; margin-bottom:12px; background:#fff; border:1px solid #e8ebef; border-radius:14px; }
.filters input,.filters select { min-height:36px; padding:6px 9px; border:1px solid #dfe3e8; border-radius:9px; font:inherit; font-size:11px; }.filters input[type=search]{min-width:260px}.filters label{font-size:11px}.filters .clear{background:#e7e9ed;color:#4b525c}
.content-grid { display:grid; grid-template-columns:1fr; gap:12px; }.content-grid.has-detail{grid-template-columns:minmax(420px,.85fr) minmax(480px,1.15fr)}
.conversation-list,.conversation-detail { background:#fff; border:1px solid #e8ebef; border-radius:15px; overflow:hidden; }
.conversation-row { width:100%; border:0; border-bottom:1px solid #eef0f3; padding:12px; display:grid; grid-template-columns:160px 1fr auto; gap:12px; align-items:center; text-align:right; background:#fff; cursor:pointer; }.conversation-row:hover,.conversation-row.active{background:#f5f8ff}
.identity{display:flex;flex-direction:column;gap:3px}.identity strong{font-size:12px}.identity small{font-size:10px;color:#747b85;text-align:right}.preview{font-size:11px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;unicode-bidi:plaintext}.row-meta{display:flex;align-items:center;gap:7px;color:#858c95;font-size:10px}.row-meta em{font-style:normal;color:#168463}.danger{color:#c73f48!important}
.empty-state{padding:50px;text-align:center;color:#8a919b;font-size:12px}.pagination{display:flex;justify-content:center;align-items:center;gap:12px;padding:12px;font-size:11px}.pagination button:disabled{opacity:.35}
.conversation-detail{max-height:calc(100vh - 190px);display:grid;grid-template-rows:auto auto 1fr;position:sticky;top:10px}.conversation-detail>header{display:flex;justify-content:space-between;align-items:flex-start;padding:14px;border-bottom:1px solid #eceff2}.conversation-detail h3{margin:0 0 4px;font-size:14px}.conversation-detail header p{margin:2px 0;color:#747b85;font-size:10px;text-align:right}.conversation-detail header button{border:0;background:transparent;font-size:25px;cursor:pointer}.detail-summary{display:flex;gap:10px;flex-wrap:wrap;padding:9px 14px;color:#7a818b;background:#fafbfc;font-size:9px}.timeline{padding:14px;overflow:auto;background:#f7f8fa}.message{max-width:88%;padding:10px 12px;margin-bottom:10px;border-radius:14px;background:#fff;border:1px solid #e6e9ed}.message-user{margin-right:auto;background:#313845;color:#fff}.message-meta{display:flex;justify-content:space-between;gap:12px;margin-bottom:7px;font-size:9px;opacity:.75}.message p{margin:0;white-space:pre-wrap;line-height:1.9;font-size:11px;unicode-bidi:plaintext}.message-assistant{margin-left:auto}.recommended-products{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px;margin-top:9px}.recommended-products a{display:grid;grid-template-columns:42px 1fr;gap:6px;padding:6px;border:1px solid #eceef1;border-radius:10px;text-decoration:none;color:#333}.recommended-products img{grid-row:span 2;width:42px;height:42px;object-fit:contain}.recommended-products span{font-size:9px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.recommended-products small{font-size:8px;color:#d55b3f}.message details{margin-top:8px;font-size:9px;color:#7a818b}.technical{display:flex;gap:8px;flex-wrap:wrap;margin-top:5px}
@media(max-width:1100px){.content-grid.has-detail{grid-template-columns:1fr}.conversation-detail{position:fixed;z-index:1000;inset:20px;max-height:none}.metrics{grid-template-columns:repeat(4,1fr)}.settings-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.api-key-field{grid-column:1/3}.assistant-content-grid{grid-template-columns:1fr}}
@media(max-width:700px){.assistant-admin{padding:7px}.metrics{grid-template-columns:repeat(2,1fr)}.conversation-row{grid-template-columns:1fr}.row-meta{flex-wrap:wrap}.filters input[type=search]{min-width:100%;width:100%}.conversation-detail{inset:0;border-radius:0}.recommended-products{grid-template-columns:1fr}.settings-grid{grid-template-columns:1fr}.api-key-field{grid-column:auto}.settings-heading{flex-direction:column}.response-rule,.starter-rule{grid-template-columns:1fr}.rule-toolbar{flex-wrap:wrap}.rule-toolbar>span{width:100%;margin:0}.starter-rule .remove-config{justify-self:start}}
</style>
